import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosHeaders } from "axios";
import { useUser } from './UserContext';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;


let currentToken: string | null = null;
let currentClientId: string | number | null = null;
let currentTenantId: string | number | null = null;

export const setAuth = (token: string | null, clientId?: string | number | null, tenantId?: string | number | null) => {
    currentToken = token;
    currentClientId = clientId ?? null;
    currentTenantId = tenantId ?? null
};

export const api = axios.create({ baseURL: process.env.REACT_APP_API_BASE_URL, timeout: 12000 });

api.interceptors.request.use((config) => {
    const headers = AxiosHeaders.from(config.headers);
    if (currentToken && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${currentToken}`);
    if (currentClientId != null && !headers.has("X-Client-Id")) headers.set("X-Client-Id", String(currentClientId));
    if (currentTenantId != null && !headers.has("X-Tenant-Id")) headers.set("X-Tenant-Id", String(currentTenantId));
    config.headers = headers;
    return config;
});

export type CreateOpts = { timeoutMs?: number };

export type AuthProviders = {
    getAccessToken: () => Promise<string | null> | string | null;
    getClientId?: () => string | number | null;
    getTenantId?: () => string | number | null;
    refreshToken?: () => Promise<void>;
};

function isAuthProviders(x: unknown): x is AuthProviders {
    return !!x && typeof (x as any).getAccessToken === "function";
}

export interface ApiClient {
    get: <T>(url: string, accessToken: string) => Promise<T>;
    post: <T>(url: string, accessToken: string, data: FormData) => Promise<T>;
    put: <T>(url: string, accessToken: string, data: FormData) => Promise<T>;
    delete: <T>(url: string, accessToken: string) => Promise<T>;
}

const isTransient = (error: AxiosError) => {
    if (error.code === "ECONNABORTED") return true; // timeout
    if (!error.response) return true;               // network/DNS during cold start
    const status = error.response.status;
    return status === 429 || (status >= 500 && status < 600); // 5xx or throttling
};

const backoff = (attempt: number, baseMs = 250, maxMs = 4000) => {
    const exp = Math.min(maxMs, baseMs * Math.pow(2, attempt));
    const jitter = Math.random() * (exp * 0.5);
    return exp + jitter;
};

export interface RetryOptions {
    retries?: number;        // max attempts (including initial)
    timeoutMs?: number;      // per-attempt timeout
}

export const createApiClient = (auth: AuthProviders, opts?: { timeoutMs?: number }): AxiosInstance => {

    const instance = axios.create({
        baseURL: BASE_URL,
        timeout: opts?.timeoutMs ?? 12000,
    });

    // Response interceptor that retries transient failures
    instance.interceptors.request.use(async (config) => {
        const headers = AxiosHeaders.from(config.headers);
        const token = await auth.getAccessToken();
        if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
        const clientId = auth.getClientId?.();
        if (clientId != null && !headers.has("X-Client-Id")) headers.set("X-Client-Id", String(clientId));

        const tenantId = auth.getTenantId?.();
        if (tenantId != null && !headers.has("X-Tenant-Id")) headers.set("X-Tenant-Id", String(tenantId));

        config.headers = headers;
        return config;
    });

    // optional: one retry on 401 with a forced refresh
    instance.interceptors.response.use(
        r => r,
        async (error: AxiosError & { config: AxiosRequestConfig & { __did401Retry?: boolean } }) => {
            if (auth.refreshToken && error.response?.status === 401 && error.config && !error.config.__did401Retry) {
                error.config.__did401Retry = true;
                await auth.refreshToken();
                return instance(error.config);
            }
            throw error;
        }
    );

    return instance;
};

export const useApiClient = () => {

    const { activeClientId, activeTenantId } = useUser();

    const get = async <T>(url: string, accessToken: string) => {

        const res = await axios.get<T>(`${BASE_URL}/${url}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || '',
                'X-Tenant-Id': activeTenantId?.toString() || ''
            }
        });
        return res.data;
    };

    const getWithParam = async <T>(url: string, accessToken: string, param: string) => {

        const res = await axios.get<T>(`${BASE_URL}/${url}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || '',
                'X-Tenant-Id': activeTenantId?.toString() || '',
                "X-Param": param,
            }
        });
        return res.data;
    };

    const getAnnon = async <T>(url: string) => {

        const res = await axios.get<T>(`${BASE_URL}/${url}`, {
            headers: {
            }
        });
        return res.data;
    };

    const post = async <T>(url: string, accessToken: string, data: FormData) => {

        const res = await axios.post<T>(`${BASE_URL}/${url}`, data, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || '',
                'X-Tenant-Id': activeTenantId?.toString() || '',
                "Content-Type": "multipart/form-data"
            }
        });
        return res.data;
    };

    const postUri = async <T>(url: string, accessToken: string) => {

        const res = await axios.post<T>(`${BASE_URL}/${url}`,null, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || '',
                'X-Tenant-Id': activeTenantId?.toString() || '',
                "Content-Type": "application/json"
            }
        });
        return res.data;
    };

    const postJson = async <T>(
        url: string,
        accessToken: string,
        data: unknown
    ): Promise<T> => {

        const res = await axios.post<T>(`${BASE_URL}/${url}`, data, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || '',
                'X-Tenant-Id': activeTenantId?.toString() || '',
                "Content-Type": "application/json"
            }
        });

        return res.data;
    };

    const postJsonBlob = async <T>(
        url: string,
        accessToken: string,
        data: unknown
    ): Promise<T> => {

        const res = await axios.post<T>(`${BASE_URL}/${url}`, data, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || '',
                'X-Tenant-Id': activeTenantId?.toString() || '',
                "Content-Type": "application/json"
            },
            responseType: 'blob' 
        });

        return res.data;
    };


    const put = async <T>(url: string, accessToken: string, data: FormData) => {
        const res = await axios.put<T>(`${BASE_URL}/${url}`, data, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || '',
                'X-Tenant-Id': activeTenantId?.toString() || '',
                "Content-Type": "multipart/form-data"
            }
        });
        return res.data;
    };

    const putJson = async <T>(
        url: string,
        accessToken: string,
        data: unknown
    ): Promise<T> => {

        const res = await axios.put<T>(`${BASE_URL}/${url}`, data, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || '',
                'X-Tenant-Id': activeTenantId?.toString() || '',
                "Content-Type": "application/json"
            }
        });

        return res.data;
    };

    const del = async <T>(url: string, accessToken: string) => {
        const res = await axios.delete<T>(`${BASE_URL}/${url}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || '',
                'X-Tenant-Id': activeTenantId?.toString() || ''
            }
        });
        return res.data;
    };

    return { get, getWithParam, getAnnon, post, postUri, postJson, postJsonBlob, put, putJson, delete: del };
};
